import { useEffect, useMemo, type ChangeEvent } from 'react';
import { PROOF_MIME_TYPES } from '@bbbk/schema';
import { G } from './glyphs';
import { fileProblem, fileSize } from './registrationRules';

// THE WELL — where a customer attaches a proof, in both drawers: the pay
// sheet's screenshot (deposit-proof) and the dogs drawer's card
// (vaccination-cards; Freddi, 2026-10-13: "follow the same procedure as
// deposit proof"). TWO STATES, EMPTY AND ATTACHED (Freddi, 2026-10-11:
// "simplify"): empty is a mark, the words and one Choose; attached is the
// picked file itself, its name and size, and Change. While it sends, the
// attached well stays exactly as it is — the button alone says it is busy.

const ACCEPT = (PROOF_MIME_TYPES as readonly string[]).join(',');

/** The picked photo itself, drawn from a blob: URL the page's CSP admits (picked-photo-preview). */
function useFilePreview(file: File | null): string | null {
  const url = useMemo(() => {
    if (!file || !file.type.startsWith('image/')) return null;
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
    return URL.createObjectURL(file);
  }, [file]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

export function Thumb({ file }: { file: File | null }) {
  const url = useFilePreview(file);
  return <span className="cp-thumb">{url && <img src={url} alt="" />}</span>;
}

export interface ProofWellProps {
  inputId: string;
  file: File | null;
  /** A file refused before it left the phone. */
  problem: string | null;
  sending: boolean;
  /** The empty well's words: *Attach the screenshot*, *Add the card*. */
  title: string;
  sub: string;
  /** What the well says of a refused file: *That file wasn’t accepted*. */
  refused: string;
  onFile: (file: File | null, problem: string | null) => void;
}

export function ProofWell({ inputId, file, problem, sending, title, sub, refused, onFile }: ProofWellProps) {
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!f) return;
    const p = fileProblem(f);
    onFile(p ? null : f, p);
  };
  if (file) {
    return (
      <label className="cp-upload dc-has up-has" htmlFor={inputId} aria-disabled={sending || undefined}>
        <Thumb file={file} />
        <div className="dc-file"><b>{file.name}</b><small>{fileSize(file.size)}</small></div>
        <span className="dc-swap">{G.camera({ size: 13 })}Change</span>
        <input id={inputId} type="file" accept={ACCEPT} onChange={pick} disabled={sending} hidden />
      </label>
    );
  }
  const bad = problem !== null;
  return (
    <label className={'cp-upload dc-drop' + (bad ? ' up-bad' : '')} htmlFor={inputId}>
      <span className="dc-drop-ic">{bad ? G.warn({ size: 17 }) : G.camera({ size: 17 })}</span>
      <div className="dc-file">
        <b>{bad ? refused : title}</b>
        <small>{bad ? problem : sub}</small>
      </div>
      <span className="mp-btn quiet sm">Choose</span>
      <input id={inputId} type="file" accept={ACCEPT} onChange={pick} hidden />
    </label>
  );
}
